/**
 * Rollout Configuration - Phased Feature Rollout
 * 
 * This configuration controls which features are available in each phase.
 * Phase-based access is an ADDITIONAL constraint layered on top of role-based access.
 * 
 * IMPORTANT:
 * - Role-based access (canAccessAdmin, requireSpecialAccess) remains authoritative
 * - Phase-based access is a UI/UX constraint, not a security boundary
 * - Changing the active phase is a one-line config change
 * 
 * Phases:
 * - PHASE_0: Founding Agent Core (minimum viable feature set)
 * - PHASE_1: Power Agent / CRM depth (enhanced features)
 * - PHASE_2: Agency Owner features (full feature set)
 */

// Active phase - change this to control rollout
// Options: 'PHASE_0', 'PHASE_1', 'PHASE_2'
export const ACTIVE_PHASE = import.meta.env.VITE_ROLLOUT_PHASE || 'PHASE_0';

// Phase hierarchy - later phases include earlier phases
const PHASE_HIERARCHY = {
  PHASE_0: 0,
  PHASE_1: 1,
  PHASE_2: 2,
};

/**
 * Check if a phase is active or has been reached
 * @param {string} requiredPhase - Required phase ('PHASE_0', 'PHASE_1', 'PHASE_2')
 * @returns {boolean} True if the required phase is active or has been reached
 */
export const isPhaseActive = (requiredPhase) => {
  const activeLevel = PHASE_HIERARCHY[ACTIVE_PHASE] || 0;
  const requiredLevel = PHASE_HIERARCHY[requiredPhase] || 0;
  return activeLevel >= requiredLevel;
};

/**
 * Feature to Phase Mapping
 * Maps routes/features to their required phase
 * Based on Page Readiness Audit
 */
export const FEATURE_PHASES = {
  // PHASE_0: Founding Agent Core
  '/agent/dashboard': 'PHASE_0',
  '/agent/itinerary/view/:id': 'PHASE_0',
  '/agent/crm/clients': 'PHASE_0',
  '/agent/crm/clients/:id': 'PHASE_0',
  '/agent/crm/clients/new': 'PHASE_0',
  '/agent/crm/trips': 'PHASE_0', // Available to all agent users
  '/agent/crm/trips/:id': 'PHASE_0', // Detail page
  '/questionnaire/:tripId': 'PHASE_0',
  '/agent/documents': 'PHASE_0', // WonderVault - basic
  '/agent/settings': 'PHASE_0',
  '/agent/notifications': 'PHASE_0',
  '/forgot-password': 'PHASE_0',
  '/reset-password': 'PHASE_0',
  
  // Client Portal - Phase 0
  '/i/:tripId': 'PHASE_0',
  '/i/:tripId/itinerary': 'PHASE_0',
  '/i/:tripId/docs': 'PHASE_0',
  '/i/:tripId/messages': 'PHASE_0',

  // PHASE_0: Destination-specific Trip Management
  '/agent/crm/disney-world': 'PHASE_0',
  '/agent/crm/disneyland': 'PHASE_0',
  '/agent/crm/universal-orlando': 'PHASE_0',
  '/agent/crm/disney-cruise': 'PHASE_0',
  '/agent/disney-world': 'PHASE_0',
  '/agent/disneyland': 'PHASE_0',
  '/agent/universal-orlando': 'PHASE_0',
  '/agent/disney-cruise': 'PHASE_0',
  
  // PHASE_1: Power Agent / CRM Depth
  '/agent/crm': 'PHASE_1',
  '/agent/crm/leads': 'PHASE_1',
  '/agent/crm/leads/:id': 'PHASE_1', // Detail page
  '/agent/crm/commissions': 'PHASE_1',
  '/agent/crm/commissions/:id': 'PHASE_1', // Detail page
  '/agent/crm/pipeline': 'PHASE_1',
  '/agent/crm/communications': 'PHASE_1',
  '/agent/crm/subscriptions': 'PHASE_1',
  '/agent/sales-pipeline': 'PHASE_1',
  
  // PHASE_2: Agency Owner Features
  '/agent/admin/dashboard': 'PHASE_2',
  '/agent/admin/agents': 'PHASE_2',
  '/agent/admin/agents/:id': 'PHASE_2',
  '/agent/admin/financial': 'PHASE_2',
  '/agent/admin/white-labeling': 'PHASE_2',
  '/agent/admin/reports': 'PHASE_2',
  '/agent/admin/activity-log': 'PHASE_2',
  '/agent/admin/attention': 'PHASE_2',
  '/agent/admin/documents': 'PHASE_2',
  '/agent/admin/clients': 'PHASE_2',
  
  // PHASE_2: Advanced CRM Features
  '/agent/crm/communications/campaigns': 'PHASE_2',
  '/agent/crm/analytics': 'PHASE_2',
  '/agent/crm/analytics/reports': 'PHASE_2',
  '/agent/crm/automation': 'PHASE_2',
  '/agent/crm/automation/followup': 'PHASE_2',
  '/agent/crm/settings': 'PHASE_2',
  
  // Client Portal - Phase 1/2 features
  '/i/:tripId/calendar': 'PHASE_1',
  '/i/:tripId/map': 'PHASE_1',
  '/i/:tripId/settings': 'PHASE_1',
};

/**
 * Check if a route/feature is available in the current phase
 * @param {string} route - Route path (supports patterns like '/agent/crm/trips/:id')
 * @returns {boolean} True if the route is available in current phase
 */
export const isRouteInPhase = (route) => {
  // Exact match first
  if (FEATURE_PHASES[route]) {
    return isPhaseActive(FEATURE_PHASES[route]);
  }
  
  // Pattern matching for dynamic routes
  // Check if route matches any pattern in FEATURE_PHASES
  for (const [pattern, phase] of Object.entries(FEATURE_PHASES)) {
    // Convert pattern to regex (simple pattern matching)
    const patternRegex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
    if (patternRegex.test(route)) {
      return isPhaseActive(phase);
    }
  }
  
  // Default: if not in mapping, assume Phase 0 (available)
  return true;
};

/**
 * Get the phase name for a route
 * @param {string} route - Route path
 * @returns {string|null} Phase name or null if not found
 */
export const getRoutePhase = (route) => {
  // Exact match first
  if (FEATURE_PHASES[route]) {
    return FEATURE_PHASES[route];
  }
  
  // Pattern matching
  for (const [pattern, phase] of Object.entries(FEATURE_PHASES)) {
    const patternRegex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
    if (patternRegex.test(route)) {
      return phase;
    }
  }
  
  return null;
};

/**
 * Get friendly phase name
 * @param {string} phase - Phase constant ('PHASE_0', 'PHASE_1', 'PHASE_2')
 * @returns {string} Friendly phase name
 */
export const getPhaseName = (phase) => {
  const names = {
    PHASE_0: 'Founding Agent Core',
    PHASE_1: 'Power Agent / CRM Depth',
    PHASE_2: 'Agency Owner Features',
  };
  return names[phase] || phase;
};

/**
 * Get next phase name (for "coming soon" messages)
 * @param {string} currentPhase - Current phase
 * @returns {string} Next phase name
 */
export const getNextPhaseName = (currentPhase) => {
  if (currentPhase === 'PHASE_0') return 'Phase 1';
  if (currentPhase === 'PHASE_1') return 'Phase 2';
  return null;
};
